// VideoPlayer.js
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  FaPlay,
  FaPause,
  FaStepBackward,
  FaStepForward,
  FaExpand,
  FaSlash,
  FaVolumeDown,
  FaVolumeUp
} from 'react-icons/fa';
import './VideoPlayer.css';
import StaticOverlay from './StaticOverlay';
import useWhiteNoise from './useWhiteNoise';

const playlist = [
  {
    title: 'Neutrals Trailer',
    camTitle: 'NEUTRALS_TRLR.AVI',
    sources: [
      <source key='neutrals-trailer-hev1' src='https://assets.2jakes.tv/neutrals-trailer-hev1.mp4' type='video/mp4; codecs="hev1"' />,
      <source key='neutrals-trailer-hvc1' src='https://assets.2jakes.tv/neutrals-trailer-hvc1.mp4' type='video/mp4; codecs="hvc1"' />,
      <source key='neutrals-trailer-h264' src='https://assets.2jakes.tv/neutrals-trailer-h264.mp4' type='video/mp4' />
    ],
    poster: `${process.env.PUBLIC_URL}/videos/poster-neutrals-trailer.jpg`,
  },
  {
    title: 'Boiling Point',
    camTitle: 'BOILING_PT.MOV',
    sources: [
      <source key='boiling-point-hev1' src='https://assets.2jakes.tv/boiling-point-hev1.mp4' type='video/mp4; codecs="hev1"' />,
      <source key='boiling-point-hvc1' src='https://assets.2jakes.tv/boiling-point-hvc1.mp4' type='video/mp4; codecs="hvc1"' />,
      <source key='boiling-point-h264' src='https://assets.2jakes.tv/boiling-point-h264.mp4' type='video/mp4' />
    ],
    poster: `${process.env.PUBLIC_URL}/videos/poster-boiling-point.jpg`,
  },
  {
    title: 'Y Dontcha Go on n Ask Urself!',
    camTitle: 'ASKURSELF.MOV',
    sources: [
      <source key='askurself-hev1' src='https://assets.2jakes.tv/askurself-hev1.mp4' type='video/mp4; codecs="hev1"' />,
      <source key='askurself-hvc1' src='https://assets.2jakes.tv/askurself-hvc1.mp4' type='video/mp4; codecs="hvc1"' />,
      <source key='askurself-h264' src='https://assets.2jakes.tv/askurself-h264.mp4' type='video/mp4' />
    ],
    poster: `${process.env.PUBLIC_URL}/videos/poster-askurself.jpg`,
  },
  {
    title: 'HIGH-JINKS',
    camTitle: 'HIGHJINKS.AVI',
    sources: [
      <source key='high-jinks-spoof-hev1' src='https://assets.2jakes.tv/high-jinks-spoof-hev1.mp4' type='video/mp4; codecs="hev1"' />,
      <source key='high-jinks-spoof-hvc1' src='https://assets.2jakes.tv/high-jinks-spoof-hvc1.mp4' type='video/mp4; codecs="hvc1"' />,
      <source key='high-jinks-spoof-h264' src='https://assets.2jakes.tv/high-jinks-spoof-h264.mp4' type='video/mp4' />
    ],
    poster: `${process.env.PUBLIC_URL}/videos/poster-high-jinks-spoof.jpg`,
  },
  {
    title: 'Stevenson Campaign Ad',
    camTitle: 'STEVENSN_AD.MOV',
    sources: [
      <source key='stevenson-campaign-ad-hev1' src='https://assets.2jakes.tv/stevenson-campaign-ad-hev1.mp4' type='video/mp4; codecs="hev1"' />,
      <source key='stevenson-campaign-ad-hvc1' src='https://assets.2jakes.tv/stevenson-campaign-ad-hvc1.mp4' type='video/mp4; codecs="hvc1"' />,
      <source key='stevenson-campaign-ad-h264' src='https://assets.2jakes.tv/stevenson-campaign-ad-h264.mp4' type='video/mp4' />
    ],
    poster: `${process.env.PUBLIC_URL}/videos/poster-stevenson-ad.jpg`,
  },
  {
    title: 'Hell Tide (OAR)',
    camTitle: 'HELL_TIDE_OAR.AVI',
    sources: [
      <source key='hell-tide-hev1' src='https://assets.2jakes.tv/hell-tide-hev1.mp4' type='video/mp4; codecs="hev1"' />,
      <source key='hell-tide-hvc1' src='https://assets.2jakes.tv/hell-tide-hvc1.mp4' type='video/mp4; codecs="hvc1"' />,
      <source key='hell-tide-h264' src='https://assets.2jakes.tv/hell-tide-h264.mp4' type='video/mp4' />
    ],
    poster: `${process.env.PUBLIC_URL}/videos/poster-hell-tide.jpg`,
  },
  {
    title: 'Neutrals Q&A',
    camTitle: 'NTRLS_QA_01.MOV',
    sources: [
      <source key='neutrals-1-25-26-qa-hev1' src='https://assets.2jakes.tv/neutrals-1-25-26-qa-hev1.mp4' type='video/mp4; codecs="hev1"' />,
      <source key='neutrals-1-25-26-qa-hvc1' src='https://assets.2jakes.tv/neutrals-1-25-26-qa-hvc1.mp4' type='video/mp4; codecs="hvc1"' />,
      <source key='neutrals-1-25-26-qa-h264' src='https://assets.2jakes.tv/neutrals-1-25-26-qa-h264.mp4' type='video/mp4' />
    ],
    poster: `${process.env.PUBLIC_URL}/videos/poster-neutrals-1-25-26-qa.jpg`,
  },
];

const VideoPlayer = forwardRef(({ autoPlay = true }, ref) => {
  const videoRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [fullscreenSupported, setFullscreenSupported] = useState(true);
  const [volume, setVolume] = useState(1.0);
  const [showStatic, setShowStatic] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [camFading, setCamFading] = useState(false);

  useWhiteNoise(showStatic, volume * 0.2);

  useImperativeHandle(ref, () => ({
    play: () => videoRef.current?.play(),
    pause: () => videoRef.current?.pause(),
  }));

  // Reset cam title when video changes
  useEffect(() => {
    setCamFading(false);
  }, [current]);

  // Advance to next (loops at end)
  const next = () => {
    setShowStatic(true);
    setCurrent(i => (i + 1) % playlist.length);
  };

  // Back up to the previous entry (loops at the start)
  const prev = () => {
    setShowStatic(true);
    setCurrent(i => (i - 1 + playlist.length) % playlist.length);
  };

  // Single play/pause toggle (mirrors J2nZ's togglePlayPause) instead of
  // separate always-visible Play and Pause buttons. Just flips state —
  // the play/pause-sync effect below is what actually calls play()/
  // pause() on the <video> element.
  const togglePlayPause = () => setIsPlaying(p => !p);

  // Reaching the end of a video fires a native 'pause' event just before
  // 'ended' (per spec, the element is marked paused as part of the
  // "reaches the end" steps) — so by the time this runs, the pause-DOM-
  // event listener below has already set isPlaying to false. Explicitly
  // setting it back to true is safe (you can't reach "ended" without
  // having been playing) and is what makes the playlist keep advancing
  // instead of loading the next video already paused.
  const handleEnded = () => {
    next();
    setIsPlaying(true);
  };

  // When a new video is selected, just (re)load it and run the static-
  // overlay timing — NOT play/pause, which the effect below owns.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    setCurrentTime(0);
    v.load();

    const start = performance.now();
    const handleCanPlay = () => {
      const elapsed = performance.now() - start;
      const MIN_STATIC_MS = 500;
      const remaining = Math.max(0, MIN_STATIC_MS - elapsed);
      /* 3. Guarantee some static; hide afterwards */
      setTimeout(() => setShowStatic(false), remaining);
      v.removeEventListener('canplay', handleCanPlay);
    };
    v.addEventListener('canplay', handleCanPlay, { once: true });
  }, [current]);

  // Keep the <video> element's actual play/pause state in sync with
  // `isPlaying` — including right after the load effect above swaps in
  // a new source. Mirrors MusicPlayer's play/pause effect (same
  // reasoning: reload the source only when it actually changes, then
  // apply play/pause unconditionally from state) so that skipping to
  // the next/previous video continues playing if it was already
  // playing instead of pausing regardless of what was happening before.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) {
      const p = v.play();
      if (p && p.catch) {
        p.catch((err) => {
          // Autoplay blocked by the browser (e.g. unmuted autoplay before
          // any user interaction). No 'pause' event fires in that case, so
          // the listener below never corrects isPlaying — do it here, or
          // the button shows Pause over a stopped video. Other rejections
          // (AbortError when load() interrupts play() on skip) are
          // expected and must not flip state, or skipping would pause.
          if (err && err.name === 'NotAllowedError') setIsPlaying(false);
        });
      }
    } else {
      v.pause();
    }
  }, [current, isPlaying]);

  // Whenever volume changes, update the <video> element
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, [volume]);

  // Track real playback state via DOM events
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const handlePlay = () => { setIsPlaying(true); setCamFading(true); };
    const handlePause = () => setIsPlaying(false);
    v.addEventListener('play', handlePlay);
    v.addEventListener('pause', handlePause);
    return () => {
      v.removeEventListener('play', handlePlay);
      v.removeEventListener('pause', handlePause);
    };
  }, []);

  // Fullscreen toggle — supports standard, webkit (macOS Safari), and webkitEnterFullscreen (iOS Safari)
  const toggleFull = () => {
    const el = videoRef.current;
    if (!el) return;

    const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement;

    if (isFullscreen) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
    } else {
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      } else if (el.webkitEnterFullscreen) {
        el.webkitEnterFullscreen();
      }
    }
  };

  const fill = `${volume * 100}%`;

  const handleSeek = e => {
    const bar = e.currentTarget;
    const ratio = (e.clientX - bar.getBoundingClientRect().left) / bar.offsetWidth;
    if (videoRef.current) videoRef.current.currentTime = ratio * duration;
  };

  // Proactively detect fullscreen support; also catch errors from any browser
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (!el.requestFullscreen && !el.webkitRequestFullscreen && !el.webkitEnterFullscreen) {
      setFullscreenSupported(false);
    }

    const handleFullscreenError = () => setFullscreenSupported(false);
    el.addEventListener('fullscreenerror', handleFullscreenError);
    el.addEventListener('webkitfullscreenerror', handleFullscreenError);

    return () => {
      el.removeEventListener('fullscreenerror', handleFullscreenError);
      el.removeEventListener('webkitfullscreenerror', handleFullscreenError);
    };
  }, []);

  return (
    <div className="video-player-container">
      {/* Bezel: the DVD-player-styled frame around the screen — rounded
          aluminum chrome and a nameplate — settled on in the design canvas
          exploration. The screen itself (video + OSD + static transition)
          is unchanged inside it. */}
      <div className="video-player-bezel">
        <div className="video-player-element-container">
          <video
            ref={videoRef}
            className="video-player-element"
            controls={false}
            onEnded={handleEnded}
            onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
            onLoadedMetadata={() => setDuration(videoRef.current?.duration ?? 0)}
            poster={playlist[current].poster}
            preload="metadata"
            playsInline={true}
            style={{ visibility: showStatic ? 'hidden' : 'visible' }}
          >
            {playlist[current].sources.map((item, _idx) => item)}
            Your browser doesn’t support HTML5 video.
          </video>
          <div className={`cam-title${camFading ? ' fading' : ''}`}>{playlist[current].camTitle}</div>
          <StaticOverlay show={showStatic} />
        </div>
        <div className="video-player-nameplate" aria-hidden="true">2JAKES DVD-2000</div>
      </div>
      <div className="progress-container" onClick={handleSeek}>
        <div className="progress-bar" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
      </div>
      {/* Controls: CSS grid (not a plain centered flex row) so the button
          cluster is truly centered regardless of the grille/volume widths
          on either side — same approach as the design canvas mockup. */}
      <div className="video-controls controls">
        <div className="video-controls-grille" aria-hidden="true"></div>
        <div className="video-controls-buttons">
          <button onClick={prev} className="control-button" aria-label="Previous">
            <FaStepBackward />
          </button>
          <button
            onClick={togglePlayPause}
            className="control-button control-button--primary"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <FaPause /> : <FaPlay className="icon-play" />}
          </button>
          <button onClick={next} className="control-button" aria-label="Next">
            <FaStepForward />
          </button>
          <button onClick={toggleFull}
            className="control-button"
            aria-label="Fullscreen"
            disabled={!fullscreenSupported}
            style={!fullscreenSupported ? { pointerEvents: "none" } : {}}>
            {!fullscreenSupported ? <FaSlash style={{ position: "absolute" }} /> : null}
            <FaExpand />
          </button>
        </div>
        <div className="volume-control">
          <FaVolumeDown className="vol-icon" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={e => setVolume(parseFloat(e.target.value))}
            className="volume-slider"
            aria-label="Volume"
            style={{
              background: `linear-gradient(to right, var(--player-volume-fill) 0%, var(--player-volume-fill) ${fill}, #999 ${fill}, #999 100%)`
            }}
          />
          <FaVolumeUp className="vol-icon" />
        </div>
      </div>
    </div>
  );
});

export default VideoPlayer;
